import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ICompanyTeamsSettings } from '@/modules/settings/Team/types';
import axiosClient from '@/shared/lib/axios';

//********** Get team members ************* */

const getCompanyTeam = async (
  params: ICompanyTeamsSettings.IGetCompanyTeamParams
): Promise<ICompanyTeamsSettings.IGetCompanyTeamResponse> => {
  const { data } = await axiosClient.get(
    '/company/portalInsights/settings/team/users',
    { params }
  );
  return data;
};

export const useGetCompanyTeam = (
  params: ICompanyTeamsSettings.IGetCompanyTeamParams
) => {
  return useQuery({
    queryKey: ['getCompanyTeam', params],
    queryFn: () => getCompanyTeam(params),
    enabled: !!params.companyId && !!params.pageNumber && !!params.pageSize,
  });
};

//********** Get roles ************* */

const getTeamRoles = async (): Promise<
  ICompanyTeamsSettings.ITeamMemberRole[]
> => {
  const { data } = await axiosClient.get(
    '/company/portalInsights/settings/team/roles'
  );
  return data;
};

export const useGetTeamRoles = () => {
  return useQuery({
    queryKey: ['getTeamRoles'],
    queryFn: getTeamRoles,
  });
};

//********** Add team member ************* */

const addTeamMember = async (obj: ICompanyTeamsSettings.IAddTeamMemberDTO) => {
  const { data } = await axiosClient.post(
    '/company/portalInsights/settings/team/user',
    obj
  );
  return data;
};

export const useAddTeamMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addTeamMember,
    onSuccess: () => {
      queryClient.invalidateQueries(['getCompanyTeam']);
      queryClient.invalidateQueries(['getOnboardingStatus']);
      queryClient.invalidateQueries(['getWorkforceResourcesSupervisors']);
    },
  });
};
//********** Edit team member ************* */

const editTeamMember = async ({
  id,
  obj,
}: {
  id: number;
  obj: ICompanyTeamsSettings.IAddTeamMemberDTO;
}) => {
  const { data } = await axiosClient.patch(
    `/company/portalInsights/team-member/${id}`,
    obj
  );
  return data;
};

export const useEditTeamMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editTeamMember,
    onSuccess: () => {
      queryClient.invalidateQueries(['getCompanyTeam']);
      queryClient.invalidateQueries(['getWorkforceResourcesSupervisors']);
    },
  });
};
//********** Delete team member ************* */

const deleteTeamMember = async (id: number) => {
  const { data } = await axiosClient.delete(
    `/company/portalInsights/team-member/${id}`
  );
  return data;
};

export const useDeleteTeamMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTeamMember,
    onSuccess: () => {
      queryClient.invalidateQueries(['getCompanyTeam']);
    },
  });
};
